import PremiumGate from '@/components/premium/PremiumGate.tsx';
import { playerSidebar } from '@/config/sidebarConfig.ts';
import { getMyStatsApi } from '@/lib/api/usersApi';
import { useAuthStore } from '@/stores/auth-store';
import { COMPETITION_TYPE_LABELS as TYPE_LABELS, isUserPlusActive } from '@shared';
import { CompetitionType } from '@shared/enums/domain.enum';
import type { PlayerStatsPartner } from '@shared/types/entities';
import { useQuery } from '@tanstack/react-query';
import { BarChart2, Medal, Target, Trophy, Users } from 'lucide-react';

import DashboardLayout from '../../components/layout/DashboardLayout';
import PageHeader from '../../components/ui/PageHeader';
import QueryErrorState from '../../components/ui/QueryErrorState';
import StatCard from '../../components/ui/StatCard';

const MONTH_LABELS = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];

function getMonthLabel(month: string) {
  const [, m] = month.split('-');
  return MONTH_LABELS[parseInt(m, 10) - 1] ?? month;
}

const PARTNER_GRADIENTS = [
  'from-emerald-500 to-green-600',
  'from-blue-500 to-indigo-600',
  'from-purple-500 to-indigo-600',
  'from-orange-500 to-red-600',
  'from-teal-500 to-cyan-600',
];

function getInitials(p: PlayerStatsPartner) {
  const { firstName, lastName, username } = p;
  if (firstName && lastName) return `${firstName[0]}${lastName[0]}`.toUpperCase();
  if (firstName) return firstName.slice(0, 2).toUpperCase();
  return username.slice(0, 2).toUpperCase();
}

function formatRank(rank: number | null) {
  if (rank === null) return '—';
  if (rank === 1) return '1er';
  return `${rank}e`;
}

function getRankColor(rank: number | null) {
  if (rank === 1) return 'text-yellow-600';
  if (rank === 2) return 'text-slate-500';
  if (rank === 3) return 'text-amber-700';
  return 'text-slate-600';
}

export default function PlayerStats() {
  const userId = useAuthStore(s => s.user)?.id;
  const isAuthenticated = useAuthStore(s => s.isAuthenticated);

  const user = useAuthStore(s => s.user);
  const isPlusActive = isUserPlusActive(user?.userPlan, user?.subscriptionStatus);

  const {
    data: stats,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ['player', 'stats', userId],
    queryFn: getMyStatsApi,
    enabled: !!userId && isAuthenticated && isPlusActive,
    staleTime: 5 * 60 * 1000,
  });

  const maxMonthly = stats ? Math.max(...(stats.monthly ?? []).map(m => m.total), 1) : 1;

  if (isError) {
    return (
      <DashboardLayout sidebarConfig={playerSidebar}>
        <div className='p-8'>
          <QueryErrorState onRetry={refetch} />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout sidebarConfig={playerSidebar}>
      <PremiumGate
        isAllowed={true}
        // isAllowed={isPlusActive}
        title='Mes statistiques (Premium)'
        description='Abonnez-vous pour débloquer l’analyse complète de vos performances.'
        ctaLabel='Voir l’abonnement'
        ctaTo='/player/preferences?tab=subscription'
      >
        <div className='p-8'>
          <PageHeader title='Mes Statistiques' subtitle='Analyse de vos performances (Beta Gratuite)' />

          {/* Stats Cards */}
          <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8'>
            <StatCard
              icon={Trophy}
              iconBg='bg-yellow-100'
              iconColor='text-yellow-600'
              label='Victoires'
              value={isLoading ? '—' : String(stats?.overview.wins ?? 0)}
              subtitle={isLoading ? '' : `${stats?.overview.podiums ?? 0} podiums`}
            />
            <StatCard
              icon={Target}
              iconBg='bg-emerald-100'
              iconColor='text-emerald-600'
              label='Taux de victoire'
              value={isLoading ? '—' : `${stats?.overview.winRate ?? 0}%`}
              subtitle={isLoading ? '' : `${stats?.overview.totalCompetitions ?? 0} concours joués`}
            />
            <StatCard
              icon={BarChart2}
              iconBg='bg-blue-100'
              iconColor='text-blue-600'
              label='Concours'
              value={isLoading ? '—' : String(stats?.overview.totalCompetitions ?? 0)}
              subtitle={
                isLoading
                  ? ''
                  : stats?.overview.bestRank != null
                    ? `Meilleur classement : ${stats.overview.bestRank}e`
                    : 'Pas encore de résultat'
              }
            />
            <StatCard
              icon={Medal}
              iconBg='bg-purple-100'
              iconColor='text-purple-600'
              label='Points totaux'
              value={isLoading ? '—' : (stats?.overview.totalPoints ?? 0).toLocaleString('fr-FR')}
            />
          </div>

          <div className='grid grid-cols-1 lg:grid-cols-3 gap-8'>
            {/* Charts (2/3) */}
            <div className='lg:col-span-2 space-y-6'>
              {/* Monthly activity bar chart */}
              <div className='bg-gradient-to-br from-orange-500 to-red-600 rounded-2xl p-8 text-white'>
                <h3 className='font-bold text-lg mb-2'>Activité mensuelle</h3>
                <p className='text-orange-100 text-sm mb-6'>12 derniers mois</p>
                {isLoading ? (
                  <div className='h-48 animate-pulse bg-white/10 rounded-lg' />
                ) : (
                  <div className='h-48 flex items-end gap-3'>
                    {(stats?.monthly ?? []).map(m => (
                      <div
                        key={m.month}
                        className='flex-1 bg-white/20 rounded-t-lg hover:bg-white/30 transition-colors'
                        style={{
                          height: m.total > 0 ? `${Math.round((m.total / maxMonthly) * 100)}%` : '3px',
                        }}
                      />
                    ))}
                  </div>
                )}
                <div className='flex justify-between mt-2 text-xs text-orange-200'>
                  {(stats?.monthly ?? Array.from({ length: 12 }, (_, i) => ({ month: `x-${i + 1}` }))).map(m => (
                    <span key={m.month}>{getMonthLabel(m.month)}</span>
                  ))}
                </div>
              </div>

              {/* Performances par type */}
              <div className='bg-gradient-to-br from-blue-600 to-indigo-700 rounded-2xl p-8 text-white'>
                <h3 className='font-bold text-lg mb-6'>Performances par type</h3>
                {isLoading ? (
                  <div className='grid grid-cols-3 gap-6'>
                    {[0, 1, 2].map(i => (
                      <div key={i} className='bg-white/10 rounded-xl p-4 animate-pulse h-24' />
                    ))}
                  </div>
                ) : !stats?.byType?.length ? (
                  <p className='text-blue-200 text-sm'>Aucune donnée disponible</p>
                ) : (
                  <div className='grid grid-cols-3 gap-6'>
                    {stats.byType.map(t => (
                      <div key={t.type} className='bg-white/10 backdrop-blur rounded-xl p-4 text-center'>
                        <p className='text-sm text-blue-100 mb-2'>{TYPE_LABELS[t.type as CompetitionType] ?? t.type}</p>
                        <p className='text-3xl font-extrabold'>{t.winRate}%</p>
                        <p className='text-xs text-blue-200 mt-1'>
                          {t.wins} V / {t.total} J
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Derniers résultats */}
              <div className='bg-white rounded-2xl border border-slate-200'>
                <div className='p-6 border-b border-slate-200'>
                  <h3 className='font-bold text-slate-900'>Derniers résultats</h3>
                </div>
                {isLoading ? (
                  <div className='divide-y divide-slate-100'>
                    {[0, 1, 2, 3, 4].map(i => (
                      <div key={i} className='px-6 py-4 flex gap-4 animate-pulse'>
                        <div className='h-4 bg-slate-200 rounded w-20' />
                        <div className='h-4 bg-slate-200 rounded flex-1' />
                        <div className='h-4 bg-slate-200 rounded w-16' />
                      </div>
                    ))}
                  </div>
                ) : !stats?.recentResults?.length ? (
                  <div className='p-8 text-center text-slate-500 text-sm'>Aucun résultat disponible</div>
                ) : (
                  <table className='w-full'>
                    <thead className='bg-slate-50'>
                      <tr>
                        <th scope='col' className='px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase'>
                          Date
                        </th>
                        <th scope='col' className='px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase'>
                          Concours
                        </th>
                        <th scope='col' className='px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase'>
                          Type
                        </th>
                        <th scope='col' className='px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase'>
                          Classement
                        </th>
                        <th scope='col' className='px-6 py-3 text-right text-xs font-semibold text-slate-600 uppercase'>
                          Points
                        </th>
                      </tr>
                    </thead>
                    <tbody className='divide-y divide-slate-100'>
                      {stats.recentResults.map((r, i) => (
                        <tr key={i} className='hover:bg-slate-50'>
                          <td className='px-6 py-4 text-sm text-slate-500'>
                            {new Date(r.date).toLocaleDateString('fr-FR', {
                              day: '2-digit',
                              month: 'short',
                              year: 'numeric',
                            })}
                          </td>
                          <td className='px-6 py-4 text-sm font-semibold text-slate-900'>{r.competitionName}</td>
                          <td className='px-6 py-4'>
                            <span className='px-2.5 py-1 bg-blue-100 text-blue-700 text-xs font-semibold rounded-full'>
                              {TYPE_LABELS[r.competitionType as CompetitionType] ?? r.competitionType}
                            </span>
                          </td>
                          <td className='px-6 py-4'>
                            <span className={`font-bold text-sm ${getRankColor(r.rank)}`}>{formatRank(r.rank)}</span>
                          </td>
                          <td className='px-6 py-4 text-right font-bold text-sm text-slate-900'>
                            {r.points != null ? `+${r.points}` : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>

            {/* Sidebar (1/3) */}
            <div className='space-y-6'>
              {/* Meilleurs partenaires */}
              <div className='bg-white rounded-2xl p-6 border border-slate-200'>
                <h3 className='font-bold text-slate-900 mb-4 flex items-center gap-2'>
                  <Users className='w-5 h-5 text-orange-600' />
                  Meilleurs partenaires
                </h3>
                {isLoading ? (
                  <div className='space-y-3'>
                    {[0, 1, 2].map(i => (
                      <div key={i} className='h-16 bg-slate-100 rounded-xl animate-pulse' />
                    ))}
                  </div>
                ) : !stats?.topPartners?.length ? (
                  <p className='text-slate-500 text-sm'>Aucun partenaire identifié</p>
                ) : (
                  <div className='space-y-3'>
                    {stats.topPartners.map((p, i) => (
                      <div key={p.userId} className='flex items-center gap-3 p-3 bg-slate-50 rounded-xl'>
                        <div
                          className={`w-10 h-10 bg-gradient-to-br ${PARTNER_GRADIENTS[i % PARTNER_GRADIENTS.length]} rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0`}
                        >
                          {getInitials(p)}
                        </div>
                        <div className='flex-1 min-w-0'>
                          <p className='font-semibold text-slate-900 text-sm truncate'>
                            {p.firstName && p.lastName ? `${p.firstName} ${p.lastName}` : p.username}
                          </p>
                          <p className='text-xs text-slate-500'>{p.sharedTeams} concours ensemble</p>
                        </div>
                        <span className='text-sm font-bold text-emerald-600 flex-shrink-0'>{p.winRate}%</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Badges */}
              <div className='bg-white rounded-2xl p-6 border border-slate-200'>
                <h3 className='font-bold text-slate-900 mb-4 flex items-center gap-2'>
                  <Medal className='w-5 h-5 text-yellow-600' />
                  Badges
                </h3>
                {isLoading ? (
                  <div className='grid grid-cols-3 gap-3'>
                    {[0, 1, 2, 3, 4, 5].map(i => (
                      <div key={i} className='h-20 bg-slate-100 rounded-xl animate-pulse' />
                    ))}
                  </div>
                ) : (
                  <div className='grid grid-cols-3 gap-3'>
                    {(stats?.badges ?? []).map(b => (
                      <div
                        key={b.id}
                        className={`text-center p-3 rounded-xl ${b.earned ? 'bg-slate-50' : 'bg-slate-50 opacity-30'}`}
                      >
                        <p className='text-2xl mb-1'>{b.emoji}</p>
                        <p className='text-[10px] text-slate-600 font-medium'>{b.label}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </PremiumGate>
    </DashboardLayout>
  );
}
